<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, reactive, ref, watch } from 'vue';
import ImovelAPI from 'dashboard/api/imoveis';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import LaisCard from './LaisCard.vue';
import LaisPrimaryButton from './LaisPrimaryButton.vue';

const props = defineProps({
  imovel: { type: Object, required: true },
  accountId: { type: [String, Number], default: '' },
});

const emit = defineEmits(['mortgage']);

const mode = ref('cash');
const loading = ref(false);
const result = ref(null);
const form = reactive({
  propertyValue: 0,
  downPayment: 0,
  termMonths: 360,
  annualRate: '',
  monthlyIncome: '',
  firstPurchase: false,
  hasSfh: false,
});

const numberValue = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatBRL = value =>
  value == null || value === ''
    ? '-'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      }).format(Number(value));

const propertyValue = computed(() => numberValue(form.propertyValue));
const downPayment = computed(() => numberValue(form.downPayment));
const financedValue = computed(() =>
  Math.max(propertyValue.value - downPayment.value, 0)
);
const itbi = computed(() => propertyValue.value * 0.03);
const registry = computed(() => propertyValue.value * 0.01);
const notary = computed(() => propertyValue.value * 0.006);
const cashTotal = computed(
  () => propertyValue.value + itbi.value + registry.value + notary.value
);
const financingInitialCost = computed(
  () => downPayment.value + itbi.value + registry.value + notary.value
);
const mortgageHref = computed(() =>
  props.accountId
    ? frontendURL(
        `accounts/${props.accountId}/financeiro?imovel_id=${props.imovel.id}`
      )
    : ''
);

const rows = computed(() =>
  mode.value === 'cash'
    ? [
        { label: 'Valor do imóvel', value: propertyValue.value },
        { label: 'ITBI estimado', value: itbi.value },
        { label: 'Registro', value: registry.value },
        { label: 'Cartório', value: notary.value },
        { label: 'Total à vista', value: cashTotal.value, strong: true },
      ]
    : [
        { label: 'Entrada', value: downPayment.value },
        { label: 'Valor financiado', value: financedValue.value },
        { label: 'ITBI estimado', value: itbi.value },
        { label: 'Registro + cartório', value: registry.value + notary.value },
        {
          label: 'Custo inicial',
          value: financingInitialCost.value,
          strong: true,
        },
      ]
);

const applyDefaults = () => {
  const value = numberValue(props.imovel.valorVenda || props.imovel.salePrice);
  form.propertyValue = value;
  form.downPayment = Math.round(value * 0.2);
  result.value = null;
};

const simulate = async () => {
  if (!props.imovel.id || !propertyValue.value) return;

  loading.value = true;
  try {
    const { data } = await ImovelAPI.simulateFinancing(props.imovel.id, {
      property_value: propertyValue.value,
      down_payment: downPayment.value,
      term_months: form.termMonths,
      annual_rate: form.annualRate,
      monthly_income: form.monthlyIncome,
      system: 'price',
      first_purchase: form.firstPurchase,
      has_sfh: form.hasSfh,
    });
    result.value = data;
  } catch {
    result.value = null;
    useAlert('Não foi possível simular o financiamento agora.');
  } finally {
    loading.value = false;
  }
};

const openMortgage = () => {
  emit('mortgage', {
    propertyValue: propertyValue.value,
    downPayment: downPayment.value,
    financedValue: financedValue.value,
    firstPurchase: form.firstPurchase,
    hasSfh: form.hasSfh,
  });
};

watch(() => props.imovel?.id, applyDefaults, { immediate: true });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Pagamento e financiamento" density="tight">
    <div class="grid gap-4">
      <div
        class="inline-flex rounded-lais border border-lais-border bg-lais-bg-soft p-1"
      >
        <button
          type="button"
          class="flex-1 rounded-lais-sm px-3 py-2 text-[13px] font-semibold"
          :class="
            mode === 'cash'
              ? 'bg-white text-lais-primary shadow-lais-card'
              : 'text-lais-text-muted'
          "
          @click="mode = 'cash'"
        >
          À vista
        </button>
        <button
          type="button"
          class="flex-1 rounded-lais-sm px-3 py-2 text-[13px] font-semibold"
          :class="
            mode === 'financing'
              ? 'bg-white text-lais-primary shadow-lais-card'
              : 'text-lais-text-muted'
          "
          @click="mode = 'financing'"
        >
          Financiamento
        </button>
      </div>

      <div class="grid gap-2 sm:grid-cols-2">
        <label
          class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
        >
          Valor do imóvel
          <input
            v-model.number="form.propertyValue"
            type="number"
            min="0"
            step="1000"
            class="h-9 rounded-lais-sm border border-lais-border px-3 text-[13px] text-lais-text"
          />
        </label>
        <label
          v-if="mode === 'financing'"
          class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
        >
          Entrada
          <input
            v-model.number="form.downPayment"
            type="number"
            min="0"
            step="1000"
            class="h-9 rounded-lais-sm border border-lais-border px-3 text-[13px] text-lais-text"
          />
        </label>
      </div>

      <div v-if="mode === 'financing'" class="grid gap-2 sm:grid-cols-2">
        <label
          class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
        >
          Prazo
          <input
            v-model.number="form.termMonths"
            type="number"
            min="1"
            max="420"
            class="h-9 rounded-lais-sm border border-lais-border px-3 text-[13px] text-lais-text"
          />
        </label>
        <label
          class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
        >
          Renda mensal
          <input
            v-model.number="form.monthlyIncome"
            type="number"
            min="0"
            step="500"
            class="h-9 rounded-lais-sm border border-lais-border px-3 text-[13px] text-lais-text"
          />
        </label>
        <label
          class="grid gap-1 text-[12px] font-semibold text-lais-text-muted"
        >
          Taxa anual
          <input
            v-model.number="form.annualRate"
            type="number"
            min="0"
            max="30"
            step="0.1"
            class="h-9 rounded-lais-sm border border-lais-border px-3 text-[13px] text-lais-text"
          />
        </label>
        <label class="flex items-center gap-2 text-[12px] text-lais-text-muted">
          <input v-model="form.firstPurchase" type="checkbox" />
          Primeira compra
        </label>
        <label class="flex items-center gap-2 text-[12px] text-lais-text-muted">
          <input v-model="form.hasSfh" type="checkbox" />
          SFH
        </label>
      </div>

      <div class="grid gap-2 text-[13px]">
        <div
          v-for="row in rows"
          :key="row.label"
          class="flex items-center justify-between gap-3"
        >
          <span
            :class="
              row.strong
                ? 'font-semibold text-lais-text'
                : 'text-lais-text-muted'
            "
          >
            {{ row.label }}
          </span>
          <strong class="text-lais-text">{{ formatBRL(row.value) }}</strong>
        </div>
      </div>

      <div
        v-if="result"
        class="rounded-lais-sm bg-lais-bg-muted px-3 py-2 text-[12px]"
      >
        <p class="font-semibold text-lais-text">
          Parcela inicial: {{ formatBRL(result.first_installment) }}
        </p>
        <p class="text-lais-text-muted">
          Valor financiado: {{ formatBRL(result.financed_amount) }}
        </p>
      </div>

      <div class="grid gap-2">
        <LaisPrimaryButton
          v-if="mode === 'financing'"
          icon="i-lucide-calculator"
          variant="ghost"
          :is-loading="loading"
          @click="simulate"
        >
          Recalcular financiamento
        </LaisPrimaryButton>
        <a
          v-if="mortgageHref"
          :href="mortgageHref"
          class="inline-flex min-h-10 items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
          @click="openMortgage"
        >
          <span class="i-lucide-landmark size-4" aria-hidden="true" />
          Abrir fluxo de financiamento
        </a>
      </div>
    </div>
  </LaisCard>
</template>
